import { AlertCard, Button, Input } from "@texada/ui";
import { resetPassword } from "actions/authActions";
import { hasOAuthPasswordCharacters } from "helpers/validators";
import { INTERNAL_SERVER_ERROR } from "http-status-codes";
import i18n from "i18n/i18n";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useParams } from "react-router-dom";
import MainScreen from "../MainScreen";

type Error = {
  message?: string;
  password?: string;
  checkPassword?: string;
};

type ErrorMsg = {
  message?: string;
};

function inputValidation(data) {
  let errors: Error = {};

  if (isEmpty(data.password)) {
    errors.password = "Password field is required";
  } else if (!hasOAuthPasswordCharacters(data.password)) {
    errors.password = [
      i18n.t("passwords_must_be_of_8_characters_or_more_in_length"),
      i18n.t("passwords_should_contain_letters_numbers_and_special_characters"),
      i18n.t("passwords_must_have_at_least_one_uppercase_and_one_lower_case")
    ].join(" ");
  }
  if (isEmpty(data.checkPassword)) {
    errors.checkPassword = "Confirm password field is required";
  } else if (!isEmpty(data.password) && data.password !== data.checkPassword) {
    errors.checkPassword = "Password does not match";
  }

  return {
    errors,
    isValid: isEmpty(errors)
  };
}

const ResetPassword: React.FC<{
  resetPassword: ({}) => Promise<void>;
}> = props => {
  const [password, setPassword] = useState("");
  const [checkPassword, setCheckPassword] = useState("");
  const [errors, setErrors] = useState<Error>({});
  const [errorMsg, setErrorMsg] = useState<ErrorMsg>({});
  const [isLoading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { id: resetPwdId } = useParams<{ id }>();

  let textFields = null;
  if (!isEmpty(errorMsg)) {
    textFields = (
      <>
        <AlertCard type="error" size="full" closable={false}>
          {errorMsg.message}
        </AlertCard>
        <Link className="login-link" to="/forgetpassword">
          Request reset again
        </Link>
      </>
    );
  } else if (resetSuccess) {
    textFields = (
      <>
        <AlertCard type="info" size="full" closable={false}>
          New password set succesfully
        </AlertCard>
        <Link className="login-link" to="/">
          Return to sign in
        </Link>
      </>
    );
  } else {
    textFields = (
      <>
        <Input
          className="password"
          field="password"
          label="New Password"
          type="password"
          value={password}
          error={errors.password}
          onChange={e => {
            setPassword(e.target.value);
          }}
          required
          disabled={isLoading}
        />
        <Input
          className="password"
          field="checkPassword"
          label="Confirm Password"
          type="password"
          value={checkPassword}
          error={errors.checkPassword || errors.message}
          onChange={e => {
            setCheckPassword(e.target.value);
          }}
          required
          disabled={isLoading}
        />

        <Button
          className="login-button"
          isLoading={isLoading}
          buttonProps={{ type: "submit" }}
        >
          Change Password
        </Button>

        <Link className="login-link" to="/">
          Return to sign in
        </Link>
      </>
    );
  }

  const isValid = () => {
    const { errors, isValid } = inputValidation({
      password,
      checkPassword
    });
    setErrors(errors);
    return isValid;
  };

  useEffect(() => {
    if (!isEmpty(password) || !isEmpty(checkPassword)) {
      isValid();
    }
  }, [password, checkPassword]);

  const handleSubmit = event => {
    event.preventDefault();

    if (isValid()) {
      setErrors({});
      setErrorMsg({});
      setLoading(true);
      props
        .resetPassword({
          password: password,
          uid: resetPwdId
        })
        .then(() => {
          setResetSuccess(true);
          setLoading(false);
        })
        .catch(err => {
          const errors = {
            message:
              err.response.status === INTERNAL_SERVER_ERROR
                ? i18n.t(
                    "connection_error_please_try_again_later_or_contact_texada_software"
                  )
                : err.response.data.error
          };
          setLoading(false);
          setPassword("");
          setCheckPassword("");
          setErrorMsg(errors);
        });
    }
  };

  return (
    <MainScreen>
      <form className="login_form" onSubmit={handleSubmit}>
        {textFields}
      </form>
    </MainScreen>
  );
};

const mapDispatchToProps = {
  resetPassword
};

export default connect(null, mapDispatchToProps)(ResetPassword);
